import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { subscribeToConversations } from '../../services/messagingService';
import DailyVibeEmailInviteModal from './DailyVibeEmailInviteModal';
import '../../styles/Messaging.css';
import './UserMenuCollagesSection.css';

const formatConversationTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  if (diff < 60000) return 'now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
  if (diff < 604800000) return date.toLocaleDateString(undefined, { weekday: 'short' });
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Same invite-a-friend flow as Your collages (choice → pick match / email).
 */
export default function InviteFriendDailyVibeFlow({
  open,
  onClose,
  userId,
  onInviteMatch,
  onInviteByEmail,
  onMatchInvited,
}) {
  const [step, setStep] = useState(null);
  const [matchConversations, setMatchConversations] = useState([]);
  const [partnerPrimaryPhotoByUid, setPartnerPrimaryPhotoByUid] = useState({});
  const [partnerNicknameByUid, setPartnerNicknameByUid] = useState({});
  const [convAvatarFailedByUid, setConvAvatarFailedByUid] = useState({});
  const [matchInviteSendingId, setMatchInviteSendingId] = useState(null);

  useEffect(() => {
    if (open) setStep('choice');
    else setStep(null);
  }, [open]);

  const matchPickerPartnerUidKey = useMemo(() => {
    if (!userId) return '';
    const ids = new Set();
    for (const c of matchConversations) {
      const otherId = c.participants?.find((id) => id !== userId);
      if (otherId) ids.add(otherId);
    }
    return [...ids].sort().join(',');
  }, [matchConversations, userId]);

  const pickerOpen = step === 'matches';

  useEffect(() => {
    if (!userId || !pickerOpen) return undefined;
    return subscribeToConversations(userId, setMatchConversations);
  }, [userId, pickerOpen]);

  useEffect(() => {
    setConvAvatarFailedByUid({});
  }, [matchPickerPartnerUidKey]);

  useEffect(() => {
    if (!userId || !pickerOpen) return undefined;
    const otherIds = matchPickerPartnerUidKey ? matchPickerPartnerUidKey.split(',').filter(Boolean) : [];
    if (otherIds.length === 0) {
      setPartnerPrimaryPhotoByUid({});
      setPartnerNicknameByUid({});
      return undefined;
    }
    const unsubs = otherIds.map((uid) =>
      onSnapshot(
        doc(db, 'userPublicPrimary', uid),
        (snap) => {
          const raw = snap.exists() ? snap.data()?.ad : null;
          const url =
            raw && typeof raw.userPhotoUrl === 'string' && raw.userPhotoUrl.trim()
              ? raw.userPhotoUrl.trim()
              : null;
          const nickname =
            raw && typeof raw.posterNickname === 'string' && raw.posterNickname.trim()
              ? raw.posterNickname.trim()
              : null;
          setPartnerPrimaryPhotoByUid((prev) => {
            if (prev[uid] === url) return prev;
            return { ...prev, [uid]: url };
          });
          setPartnerNicknameByUid((prev) => {
            if (prev[uid] === nickname) return prev;
            return { ...prev, [uid]: nickname };
          });
        },
        () => {
          setPartnerPrimaryPhotoByUid((prev) => {
            if (!(uid in prev)) return prev;
            const next = { ...prev };
            delete next[uid];
            return next;
          });
          setPartnerNicknameByUid((prev) => {
            if (!(uid in prev)) return prev;
            const next = { ...prev };
            delete next[uid];
            return next;
          });
        },
      ),
    );
    return () => {
      unsubs.forEach((u) => u());
    };
  }, [userId, pickerOpen, matchPickerPartnerUidKey]);

  const closeAll = () => {
    if (matchInviteSendingId) return;
    onClose?.();
  };

  const getMatchOtherParticipant = (conversation) => {
    if (!conversation?.participantDetails || !userId) {
      return { displayName: 'User', email: '' };
    }
    const otherId = conversation.participants?.find((id) => id !== userId);
    return conversation.participantDetails[otherId] || { displayName: 'User', email: '' };
  };

  const resolveMatchNickname = (otherUid, other) => {
    const fromPrimary = otherUid ? partnerNicknameByUid[otherUid] : null;
    if (fromPrimary) return fromPrimary;
    const fromConv = other?.displayName;
    if (fromConv && String(fromConv).trim()) return String(fromConv).trim();
    return 'Someone';
  };

  const handlePickMatch = async (conv) => {
    if (!conv?.id || matchInviteSendingId) return;
    const otherUid = conv.participants?.find((id) => id !== userId);
    const other = getMatchOtherParticipant(conv);
    const nickname = resolveMatchNickname(otherUid, other);
    if (typeof onInviteMatch === 'function') {
      setMatchInviteSendingId(conv.id);
      try {
        const ok = await onInviteMatch({
          conversationId: conv.id,
          otherUid,
          displayName: other.displayName,
        });
        if (ok === false) return;
      } finally {
        setMatchInviteSendingId(null);
      }
    }
    onMatchInvited?.({
      uid: otherUid || null,
      displayName: other.displayName || 'Your friend',
      nickname,
      conversationId: conv.id,
    });
    onClose?.();
  };

  if (!open || !step) return null;

  return (
    <>
      {step === 'choice' &&
        createPortal(
          <div
            className="user-menu-match-removal-confirm-overlay"
            role="presentation"
            onClick={closeAll}
          >
            <div
              className="user-menu-match-removal-confirm cart-popup vibe-popup user-menu-coupon-redeem-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="user-menu-invite-friend-choice-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="cart-popup-close"
                aria-label="Close"
                onClick={closeAll}
              >
                ×
              </button>
              <h3
                id="user-menu-invite-friend-choice-title"
                className="match-explainer-title user-menu-match-removal-confirm-title"
              >
                INVITE A FRIEND TO SEE THEIR DAILY VIBES
              </h3>
              <div className="user-menu-invite-friend-choice-actions">
                {typeof onInviteMatch === 'function' ? (
                  <button
                    type="button"
                    className="feed-tile-detail-btn feed-tile-detail-btn-refer user-menu-rewards-redeem-btn user-menu-rewards-spin-btn"
                    onClick={() => setStep('matches')}
                  >
                    PICK FROM MATCHES
                  </button>
                ) : null}
                {typeof onInviteByEmail === 'function' ? (
                  <button
                    type="button"
                    className="feed-tile-detail-btn feed-tile-detail-btn-refer user-menu-rewards-redeem-btn user-menu-rewards-spin-btn"
                    onClick={() => setStep('email')}
                  >
                    INVITE BY EMAIL
                  </button>
                ) : null}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {step === 'matches' &&
        createPortal(
          <div
            className="user-menu-match-removal-confirm-overlay"
            role="presentation"
            onClick={() => {
              if (matchInviteSendingId) return;
              closeAll();
            }}
          >
            <div
              className="user-menu-match-removal-confirm cart-popup vibe-popup user-menu-coupon-redeem-dialog user-menu-invite-match-picker-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="user-menu-invite-match-picker-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="cart-popup-close"
                aria-label="Close"
                disabled={!!matchInviteSendingId}
                onClick={() => {
                  if (matchInviteSendingId) return;
                  closeAll();
                }}
              >
                ×
              </button>
              <h3
                id="user-menu-invite-match-picker-title"
                className="match-explainer-title user-menu-match-removal-confirm-title"
              >
                SHARE VIBE CHECKS
              </h3>
              <p className="user-menu-coupon-redeem-hint user-menu-invite-match-picker-hint">
                Choose someone from your matches to invite to share daily vibe checks.
              </p>
              <div className="user-menu-invite-match-picker-list messaging-conversations">
                {matchConversations.length === 0 ? (
                  <p className="messaging-empty user-menu-invite-match-picker-empty">
                    No matches yet. Complete your daily vibe check to get new matches — they&apos;ll show up here.
                  </p>
                ) : (
                  matchConversations.map((conv) => {
                    const other = getMatchOtherParticipant(conv);
                    const unread = conv.unreadCount?.[userId] || 0;
                    const otherUid = conv.participants?.find((id) => id !== userId);
                    const matchNickname = resolveMatchNickname(otherUid, other);
                    const navbarMatchPhoto =
                      otherUid && partnerPrimaryPhotoByUid[otherUid]
                        ? partnerPrimaryPhotoByUid[otherUid]
                        : null;
                    const showConvAvatarImg =
                      navbarMatchPhoto && !convAvatarFailedByUid[otherUid];
                    const inviting = matchInviteSendingId === conv.id;
                    return (
                      <div
                        key={conv.id}
                        className={`messaging-conversation-item user-menu-invite-match-picker-item${unread > 0 ? ' unread' : ''}${inviting ? ' user-menu-invite-match-picker-item--inviting' : ''}`}
                        onClick={() => {
                          if (matchInviteSendingId) return;
                          void handlePickMatch(conv);
                        }}
                        onKeyDown={(e) => {
                          if (matchInviteSendingId) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            void handlePickMatch(conv);
                          }
                        }}
                        role="button"
                        tabIndex={matchInviteSendingId ? -1 : 0}
                        aria-busy={inviting}
                      >
                        {showConvAvatarImg ? (
                          <img
                            src={navbarMatchPhoto}
                            alt=""
                            className="messaging-conv-avatar messaging-conv-avatar-img"
                            onError={() =>
                              setConvAvatarFailedByUid((prev) => ({ ...prev, [otherUid]: true }))
                            }
                          />
                        ) : (
                          <div className="messaging-conv-avatar" aria-hidden="true">
                            {(matchNickname || 'U')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="messaging-conv-info">
                          <div className="messaging-conv-name-row">
                            <span className="messaging-conv-name user-menu-invite-match-picker-nickname">
                              {matchNickname}
                            </span>
                            <span className="messaging-conv-time">
                              {inviting ? 'inviting…' : formatConversationTime(conv.lastMessage?.timestamp)}
                            </span>
                          </div>
                          <div className="messaging-conv-preview-row">
                            <span className="messaging-conv-preview">
                              {conv.lastMessage?.senderId === userId ? 'You: ' : ''}
                              {conv.lastMessage?.text || 'No messages yet'}
                            </span>
                            {unread > 0 && <span className="messaging-conv-badge">{unread}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="user-menu-match-removal-confirm-actions user-menu-invite-match-picker-back-wrap">
                <button
                  type="button"
                  className="vibe-popup-btn match-flow-close-btn"
                  disabled={!!matchInviteSendingId}
                  onClick={closeAll}
                >
                  Back
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <DailyVibeEmailInviteModal
        open={step === 'email'}
        onClose={closeAll}
        onSendInvite={onInviteByEmail}
      />
    </>
  );
}
